import type { CartItem, WooProduct } from "../types";

export interface StockClampResult {
  quantity: number;
  capped: boolean;
  maxAllowed: number | null;
}

export function getProductStockLimit(product: WooProduct): number | null {
  if (product.stockStatus === "outofstock" || !product.purchasable) return 0;
  if (product.backordersAllowed) return null;
  if (!product.manageStock) return null;
  if (product.stockQuantity === null) return null;
  return Math.max(0, product.stockQuantity);
}

export function getCartItemStockLimit(item: CartItem): number | null {
  if (item.stockStatus === "outofstock") return 0;
  if (item.backordersAllowed) return null;
  if (!item.manageStock) return null;
  if (item.stockLimit === undefined || item.stockLimit === null) return null;
  return Math.max(0, item.stockLimit);
}

export function clampQuantity(
  product: WooProduct,
  requestedQty: number,
): StockClampResult {
  const maxAllowed = getProductStockLimit(product);
  return clampToLimit(requestedQty, maxAllowed);
}

export function clampCartItemQuantity(
  item: CartItem,
  requestedQty: number,
): StockClampResult {
  const maxAllowed = getCartItemStockLimit(item);
  return clampToLimit(requestedQty, maxAllowed);
}

function clampToLimit(requestedQty: number, maxAllowed: number | null): StockClampResult {
  const sanitized = Math.max(0, Math.floor(Number.isFinite(requestedQty) ? requestedQty : 0));

  if (maxAllowed === null) {
    return { quantity: sanitized, capped: false, maxAllowed: null };
  }

  if (sanitized > maxAllowed) {
    return { quantity: maxAllowed, capped: true, maxAllowed };
  }

  return { quantity: sanitized, capped: false, maxAllowed };
}

export function canIncreaseQuantity(
  product: WooProduct,
  currentQty: number,
): boolean {
  const maxAllowed = getProductStockLimit(product);
  if (maxAllowed === null) return true;
  return currentQty < maxAllowed;
}

export function canIncreaseCartItemQuantity(item: CartItem): boolean {
  const maxAllowed = getCartItemStockLimit(item);
  if (maxAllowed === null) return true;
  return item.quantity < maxAllowed;
}

export function snapshotStockFromProduct(product: WooProduct): Pick<
  CartItem,
  "stockLimit" | "manageStock" | "backordersAllowed" | "stockStatus"
> {
  return {
    stockLimit: product.manageStock ? product.stockQuantity : null,
    manageStock: product.manageStock,
    backordersAllowed: product.backordersAllowed,
    stockStatus: product.stockStatus,
  };
}

export function isLowStock(product: WooProduct): boolean {
  if (!product.manageStock || product.stockQuantity === null) return false;
  return product.stockQuantity > 0 && product.stockQuantity <= 3;
}
